import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Button,
  Select,
  TableSortLabel,
  InputAdornment,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import { useHistory } from "react-router-dom";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import {
  Edit as EditIcon,
  Search as SearchIcon,
  Visibility,
  VisibilityOff,
  Business as BusinessIcon,
  RotateLeft as ResetIcon,
  Refresh as RefreshIcon,
  VpnKey as VpnKeyIcon,
  MailOutline as MailOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  Close as CloseIcon,
  Add as AddIcon,
  DateRange as DateRangeIcon,
  Speed as SpeedIcon,
  Tune as TuneIcon,
  ContactMail as ContactMailIcon,
  CheckCircleOutline as CheckCircleOutlineIcon
} from "@material-ui/icons";

import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { head, isArray } from "lodash";
import { useDate } from "../../hooks/useDate";

import moment from "moment";
import { i18n } from "../../translate/i18n";
import { useTheme } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSettings from "../../hooks/useSettings";
import ResellerTransactionModal from "../ResellerTransactionModal";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "transparent",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 20px",
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
  },
  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  topBarTitle: {
    fontSize: "0.95rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  topBarIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.3rem",
  },
  countChip: {
    backgroundColor: `${theme.palette.primary.main}14`,
    color: theme.palette.primary.main,
    fontWeight: 600,
    borderRadius: 13,
    height: 26,
    border: `1px solid ${theme.palette.primary.main}4D`,
    fontSize: "0.75rem",
    padding: "0 8px",
  },
  topBarRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  searchField: {
    width: 280,
    maxWidth: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#FFFFFF",
    },
  },
  newButton: {
    borderRadius: 8,
    fontWeight: 700,
    fontSize: "0.88rem",
    textTransform: "none",
    padding: "8px 24px",
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
    boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
    transition: "all 0.25s ease",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}CC !important`,
      boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
    },
  },
  tableWrapper: {
    width: "100%",
    maxHeight: "calc(100vh - 280px)",
    overflowY: "auto",
    overflowX: "auto",
    borderRadius: 10,
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    ...theme.scrollbarStyles,
  },
  table: {
    width: "100%",
    tableLayout: "fixed",
  },
  tableHeader: {
    fontWeight: 700,
    backgroundColor: theme.palette.type === "dark" ? "#1b1e24 !important" : "#F1F5F9 !important",
    color: `${theme.palette.primary.main} !important`,
    borderBottom: `2px solid ${theme.palette.primary.main}4D`,
    position: "sticky",
    top: 0,
    zIndex: 10,
    fontSize: "0.78rem",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    padding: "10px 4px",
    whiteSpace: "nowrap",
  },
  tableHeaderSort: {
    color: `${theme.palette.primary.main} !important`,
    fontWeight: 700,
    fontSize: "0.78rem",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    "&:hover": {
      color: `${theme.palette.primary.main}CC !important`,
    },
    "&.MuiTableSortLabel-active": {
      color: `${theme.palette.primary.main} !important`,
    },
    "& .MuiTableSortLabel-icon": {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  tableRow: {
    minHeight: 64,
    height: 64,
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(0, 229, 255, 0.04) !important" : "rgba(0, 0, 0, 0.02) !important",
    },
    "& td": {
      padding: "6px 2px",
      fontSize: "0.74rem",
      verticalAlign: "middle",
      overflow: "hidden",
    },
  },
  nowrap: {
    whiteSpace: "nowrap",
  },
  actionButton: {
    padding: 2,
    margin: "0 1px",
    transition: "transform 0.2s, background-color 0.2s",
    "&:hover": {
      transform: "scale(1.15)",
    },
    "& svg": {
      fontSize: "1.0rem",
    },
  },
  btnKey: {
    color: "#FFD600",
    "&:hover": {
      backgroundColor: "rgba(255, 214, 0, 0.12)",
    },
  },
  btnMail: {
    color: "#FF9100",
    "&:hover": {
      backgroundColor: "rgba(255, 145, 0, 0.12)",
    },
  },
  btnActivate: {
    color: "#00E676",
    "&:hover": {
      backgroundColor: "rgba(0, 230, 118, 0.12)",
    },
  },
  btnEdit: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}1F`,
    },
  },
  btnDelete: {
    color: "#FF5252",
    "&:hover": {
      backgroundColor: "rgba(255, 82, 82, 0.12)",
    },
  },
  fullWidth: {
    width: "100%",
  },
  // Modal Flotante (estilo Filtros Avanzados)
  filterModalPaper: {
    maxWidth: "960px",
    width: "960px",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}40` : "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.65)",
    overflow: "hidden",
  },
  filterModalTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px 14px 24px",
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
  },
  filterModalTitleText: {
    display: "flex",
    alignItems: "center",
    fontSize: "1.1rem",
    fontWeight: 700,
    letterSpacing: "0.3px",
    color: theme.palette.primary.main,
    "& svg": {
      fontSize: "1.4rem",
      marginRight: 10,
    },
  },
  filterModalContent: {
    padding: "18px 24px !important",
    backgroundColor: theme.palette.type === "dark" ? "#121418" : "#F8FAFC",
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filterColumnBox: {
    padding: "14px 18px",
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.025)" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.07)" : "1px solid rgba(0, 0, 0, 0.08)",
    marginBottom: theme.spacing(2),
  },
  filterSectionSubheader: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.82rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1.5),
    "& svg": {
      fontSize: "1.1rem",
      marginRight: 8,
    },
  },
  filterModalActions: {
    padding: "14px 24px",
    borderTop: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
  },
  applyButton: {
    borderRadius: 8,
    fontWeight: 700,
    fontSize: "0.88rem",
    textTransform: "none",
    padding: "8px 26px",
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
    boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}CC !important`,
      boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
    },
  },
  // Contadores de Cuota
  counterCard: {
    padding: "12px 16px",
    borderRadius: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.type === "dark" ? "rgba(0, 229, 255, 0.03)" : "#F1F5F9",
    border: theme.palette.type === "dark" ? "1px solid rgba(0, 229, 255, 0.18)" : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: theme.palette.type === "dark" ? "0 2px 8px rgba(0, 0, 0, 0.2)" : "0 1px 3px rgba(0, 0, 0, 0.05)",
  },
  counterTitle: {
    fontSize: "0.75rem",
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.65)" : "#64748B",
    fontWeight: 500,
  },
  counterValue: {
    fontSize: "0.88rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#0F172A",
    marginTop: 2,
  },
  // Fechas de Vencimiento de Alto Contraste y Nitidez Profesional
  dueDateGreen: {
    color: theme.palette.type === "dark" ? "#10B981" : "#047857",
    fontWeight: 700,
    backgroundColor: theme.palette.type === "dark" ? "rgba(16, 185, 129, 0.15)" : "#ECFDF5",
    padding: "3px 8px",
    borderRadius: 6,
    border: theme.palette.type === "dark" ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid #A7F3D0",
    display: "inline-block",
    whiteSpace: "nowrap",
    fontSize: "0.75rem",
  },
  dueDateYellow: {
    color: theme.palette.type === "dark" ? "#F59E0B" : "#B45309",
    fontWeight: 700,
    backgroundColor: theme.palette.type === "dark" ? "rgba(245, 158, 11, 0.15)" : "#FFFBEB",
    padding: "3px 8px",
    borderRadius: 6,
    border: theme.palette.type === "dark" ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid #FDE68A",
    display: "inline-block",
    whiteSpace: "nowrap",
    fontSize: "0.75rem",
  },
  dueDateRed: {
    color: theme.palette.type === "dark" ? "#EF4444" : "#B91C1C",
    fontWeight: 700,
    backgroundColor: theme.palette.type === "dark" ? "rgba(239, 68, 68, 0.15)" : "#FEF2F2",
    padding: "3px 8px",
    borderRadius: 6,
    border: theme.palette.type === "dark" ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid #FECACA",
    display: "inline-block",
    whiteSpace: "nowrap",
    fontSize: "0.75rem",
  },
}));

export function CompanyModal(props) {
  const { open, onClose, onSubmit, onDelete, initialValue, loading, onResetConsumption } = props;
  const classes = useStyles();
  const theme = useTheme();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    dueDate: "",
    recurrence: "",
    password: "",
    generateInvoice: true,
    enableVision: true,
    customMaxContacts: "",
    customMaxMessages: "",
    customMaxFlows: "",
    customMaxEmails: "",
    customMaxStatusPosts: "",
    customMaxGroupPosts: "",
    customMaxCommunityPosts: "",
    customMaxWebWidgets: "",
    consumContacts: 0,
    consumMessagesMonth: 0,
    consumStatusPostsMonth: 0,
    consumGroupPostsMonth: 0,
    consumCommunityPostsMonth: 0,
    plan: null,
    storageLimit: 200,
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      try {
        const list = await listPlans();
        setPlans(list);
      } catch (err) {
        console.error(err);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (!initialValue) return;
    setRecord((prev) => {
      let formattedDueDate = initialValue.dueDate;
      if (moment(initialValue.dueDate).isValid()) {
        formattedDueDate = moment(initialValue.dueDate).format("YYYY-MM-DD");
      }
      return {
        ...prev,
        ...initialValue,
        dueDate: formattedDueDate || "",
        customMaxContacts: initialValue.customMaxContacts !== null && initialValue.customMaxContacts !== undefined ? initialValue.customMaxContacts : "",
        customMaxMessages: initialValue.customMaxMessages !== null && initialValue.customMaxMessages !== undefined ? initialValue.customMaxMessages : "",
        customMaxFlows: initialValue.customMaxFlows !== null && initialValue.customMaxFlows !== undefined ? initialValue.customMaxFlows : "",
        customMaxEmails: initialValue.customMaxEmails !== null && initialValue.customMaxEmails !== undefined ? initialValue.customMaxEmails : "",
        customMaxStatusPosts: initialValue.customMaxStatusPosts !== null && initialValue.customMaxStatusPosts !== undefined ? initialValue.customMaxStatusPosts : "",
        customMaxGroupPosts: initialValue.customMaxGroupPosts !== null && initialValue.customMaxGroupPosts !== undefined ? initialValue.customMaxGroupPosts : "",
        customMaxCommunityPosts: initialValue.customMaxCommunityPosts !== null && initialValue.customMaxCommunityPosts !== undefined ? initialValue.customMaxCommunityPosts : "",
        customMaxWebWidgets: initialValue.customMaxWebWidgets !== null && initialValue.customMaxWebWidgets !== undefined ? initialValue.customMaxWebWidgets : "",
        consumContacts: initialValue.consumContacts || 0,
        consumMessagesMonth: initialValue.consumMessagesMonth || 0,
        consumStatusPostsMonth: initialValue.consumStatusPostsMonth || 0,
        consumGroupPostsMonth: initialValue.consumGroupPostsMonth || 0,
        consumCommunityPostsMonth: initialValue.consumCommunityPostsMonth || 0,
        plan: initialValue.plan || null,
        storageLimit: initialValue.storageLimit ? Math.round(initialValue.storageLimit / (1024 * 1024)) : 200,
      };
    });
  }, [initialValue]);

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }

    const dataToSave = { ...data };
    if (dataToSave.storageLimit) {
      dataToSave.storageLimit = dataToSave.storageLimit * 1024 * 1024;
    }

    delete dataToSave.resellerBalance;
    delete dataToSave.discountPercentage;

    const customFields = [
      'customMaxContacts',
      'customMaxMessages',
      'customMaxFlows',
      'customMaxEmails',
      'customMaxStatusPosts',
      'customMaxGroupPosts',
      'customMaxCommunityPosts',
      'customMaxWebWidgets'
    ];
    customFields.forEach(field => {
      if (dataToSave[field] === "" || dataToSave[field] === undefined || dataToSave[field] === null) {
        dataToSave[field] = null;
      } else {
        dataToSave[field] = Number(dataToSave[field]);
      }
    });

    await onSubmit(dataToSave);
  };

  const handleResetConsumption = async (type) => {
    if (!record.id) return;
    try {
      const { data } = await api.post(`/companies/${record.id}/reset-consumption`, { type });
      toast.success(data.message || "Contador reseteado con éxito");
      if (data.company) {
        const { aiCredits: _ignoreAiCredits, ...companyWithoutCredits } = data.company;
        const updated = {
          ...companyWithoutCredits,
          plan: data.company.plan || record.plan
        };
        setRecord(prev => ({
          ...prev,
          ...updated
        }));
        if (typeof onResetConsumption === "function") {
          onResetConsumption(updated);
        }
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: {
          companyId: initialValue.id,
        },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toastError(e);
    }
  };

  const incrementDueDate = () => {
    const data = { ...record };
    if (data.dueDate !== "" && data.dueDate !== null) {
      const recurrence = data.recurrence || "MENSAL";
      switch (recurrence) {
        case "MENSAL":
          data.dueDate = moment(data.dueDate).add(1, "month").format("YYYY-MM-DD");
          break;
        case "BIMESTRAL":
          data.dueDate = moment(data.dueDate).add(2, "month").format("YYYY-MM-DD");
          break;
        case "TRIMESTRAL":
          data.dueDate = moment(data.dueDate).add(3, "month").format("YYYY-MM-DD");
          break;
        case "SEMESTRAL":
          data.dueDate = moment(data.dueDate).add(6, "month").format("YYYY-MM-DD");
          break;
        case "ANUAL":
          data.dueDate = moment(data.dueDate).add(12, "month").format("YYYY-MM-DD");
          break;
        default:
          data.dueDate = moment(data.dueDate).add(1, "month").format("YYYY-MM-DD");
          break;
      }
      setRecord(data);
      toast.info(`Fecha de vencimiento extendida: ${data.dueDate}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.filterModalPaper }}
    >
      <DialogTitle disableTypography className={classes.filterModalTitle}>
        <div className={classes.filterModalTitleText}>
          <BusinessIcon />
          <span>{record.id ? (i18n.t("companyModal.title.editCompany", { name: record.name || `ID ${record.id}` }) || `Editar Empresa: ${record.name || `ID ${record.id}`}`) : (i18n.t("compaies.newCompany") || "Nueva Empresa")}</span>
          {record.status !== undefined && (
            <Chip
              size="small"
              label={record.status ? (i18n.t("compaies.status.active") || "Activa") : (i18n.t("compaies.status.pending") || "Pendiente")}
              style={{
                marginLeft: 14,
                backgroundColor: record.status ? "rgba(0, 230, 118, 0.15)" : "rgba(255, 145, 0, 0.15)",
                color: record.status ? "#00E676" : "#FF9100",
                fontWeight: 700,
                border: `1px solid ${record.status ? "rgba(0, 230, 118, 0.4)" : "rgba(255, 145, 0, 0.4)"}`,
                fontSize: "0.72rem",
              }}
            />
          )}
        </div>
        <IconButton
          size="small"
          onClick={onClose}
          style={{ color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748B" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={record}
        onSubmit={(values) => handleSubmit(values)}
      >
        {({ values }) => (
          <Form className={classes.fullWidth} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            <DialogContent dividers className={classes.filterModalContent}>
              {/* SECCIÓN 1: DATOS GENERALES Y ACCESO */}
              <div className={classes.filterColumnBox}>
                <Typography className={classes.filterSectionSubheader}>
                  <ContactMailIcon />
                  {i18n.t("companyModal.sections.companyData") || "Datos de la Empresa y Acceso"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.name")}
                      name="name"
                      variant="outlined"
                      fullWidth
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.email")}
                      name="email"
                      variant="outlined"
                      fullWidth
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.password")}
                      name="password"
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      fullWidth
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.phone")}
                      name="phone"
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </div>

              {/* SECCIÓN 2: PLAN, FACTURACIÓN Y CONFIGURACIÓN */}
              <div className={classes.filterColumnBox}>
                <Typography className={classes.filterSectionSubheader}>
                  <TuneIcon />
                  {i18n.t("companyModal.sections.planBilling") || "Plan, Facturación y Parámetros"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl size="small" variant="outlined" fullWidth>
                      <InputLabel htmlFor="plan-selection">{i18n.t("compaies.table.plan")}</InputLabel>
                      <Field
                        as={Select}
                        id="plan-selection"
                        label={i18n.t("compaies.table.plan")}
                        name="planId"
                        required
                      >
                        {plans.map((plan, key) => (
                          <MenuItem key={key} value={plan.id}>
                            {plan.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl size="small" variant="outlined" fullWidth>
                      <InputLabel htmlFor="status-selection">{i18n.t("compaies.table.status") || "Estado"}</InputLabel>
                      <Field
                        as={Select}
                        id="status-selection"
                        label={i18n.t("compaies.table.status") || "Estado"}
                        name="status"
                      >
                        <MenuItem value={true}>{i18n.t("compaies.status.active") || "Activa"}</MenuItem>
                        <MenuItem value={false}>{i18n.t("compaies.status.pending") || "Pendiente"}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl size="small" variant="outlined" fullWidth>
                      <InputLabel htmlFor="generate-invoice-selection">{i18n.t("compaies.table.generateInvoice")}</InputLabel>
                      <Field
                        as={Select}
                        id="generate-invoice-selection"
                        label={i18n.t("compaies.table.generateInvoice")}
                        name="generateInvoice"
                      >
                        <MenuItem value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <Field
                        as={TextField}
                        label={i18n.t("compaies.table.dueDate")}
                        type="date"
                        name="dueDate"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        fullWidth
                        size="small"
                        disabled={record.id === 1}
                      />
                      {record.id !== 1 && (
                        <Tooltip title={i18n.t("compaies.tooltips.extendCycle") || "Extender 1 Ciclo / Mes"}>
                          <IconButton
                            size="small"
                            onClick={incrementDueDate}
                            style={{ color: theme.palette.primary.main, border: `1px solid ${theme.palette.primary.main}4D`, borderRadius: 8, padding: 6 }}
                          >
                            <DateRangeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.storageLimit")}
                      name="storageLimit"
                      size="small"
                      variant="outlined"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.aiCredits")}
                      name="aiCredits"
                      size="small"
                      variant="outlined"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl size="small" variant="outlined" fullWidth>
                      <InputLabel htmlFor="enable-vision-selection">{i18n.t("compaies.table.enableVision")}</InputLabel>
                      <Field
                        as={Select}
                        id="enable-vision-selection"
                        label={i18n.t("compaies.table.enableVision")}
                        name="enableVision"
                      >
                        <MenuItem value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Field
                          as={Switch}
                          color="primary"
                          name="isReseller"
                          checked={values.isReseller}
                        />
                      }
                      label={i18n.t("compaies.table.resellerToggle") || "Es Reseller"}
                    />
                  </Grid>

                  {values.isReseller && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <Field
                          as={TextField}
                          label={i18n.t("compaies.table.resellerCommission") || "Comisión %"}
                          name="discountPercentage"
                          type="number"
                          variant="outlined"
                          fullWidth
                          size="small"
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Field
                          as={TextField}
                          label={i18n.t("compaies.table.resellerBalance") || "Saldo Reseller"}
                          name="resellerBalance"
                          type="number"
                          variant="outlined"
                          fullWidth
                          size="small"
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={4} style={{ display: "flex", alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          style={{ borderColor: "#7C4DFF", color: "#B388FF", fontWeight: 700, borderRadius: 8, textTransform: "none", width: "100%" }}
                          onClick={() => setTransactionModalOpen(true)}
                        >
                          Historial y Recargas
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>
              </div>

              {/* SECCIÓN 3: CONSUMO Y GESTIÓN DE CUOTAS */}
              {record.id && (
                <div className={classes.filterColumnBox}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <Typography className={classes.filterSectionSubheader} style={{ marginBottom: 0 }}>
                      <SpeedIcon />
                      {i18n.t("companyModal.consumption.title") || "Consumo y Gestión de Cuotas"}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleResetConsumption("all")}
                      style={{ color: "#ff5252", borderColor: "rgba(255, 82, 82, 0.4)", textTransform: "none", fontSize: "0.75rem", borderRadius: 8 }}
                      startIcon={<ResetIcon fontSize="small" />}
                    >
                      {i18n.t("companyModal.consumption.resetAll") || "Resetear Todo a 0"}
                    </Button>
                  </div>

                  <Grid container spacing={2} style={{ padding: "0 4px" }}>
                    {/* Contactos */}
                    <Grid item xs={12} sm={6} md={4}>
                      <div className={classes.counterCard}>
                        <Box>
                          <Typography className={classes.counterTitle}>
                            {i18n.t("companyModal.consumption.contacts")}
                          </Typography>
                          <Typography className={classes.counterValue}>
                            {record.consumContacts || 0} / {values.customMaxContacts !== "" && values.customMaxContacts !== null ? `${values.customMaxContacts} (pers.)` : (record.plan?.maxContacts === -1 ? "Ilimitado" : (record.plan?.maxContacts || 0))}
                          </Typography>
                        </Box>
                        <Tooltip title={i18n.t("companyModal.consumption.resetCounterTooltip") || "Resetear contador"}>
                          <IconButton size="small" onClick={() => handleResetConsumption("contacts")}>
                            <ResetIcon fontSize="small" style={{ color: theme.palette.primary.main }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </Grid>

                    {/* Mensajes */}
                    <Grid item xs={12} sm={6} md={4}>
                      <div className={classes.counterCard}>
                        <Box>
                          <Typography className={classes.counterTitle}>
                            {i18n.t("companyModal.consumption.messages")}
                          </Typography>
                          <Typography className={classes.counterValue}>
                            {record.consumMessagesMonth || 0} / {values.customMaxMessages !== "" && values.customMaxMessages !== null ? `${values.customMaxMessages} (pers.)` : (record.plan?.messagesLimit === -1 ? "Ilimitado" : (record.plan?.messagesLimit || 0))}
                          </Typography>
                        </Box>
                        <Tooltip title={i18n.t("companyModal.consumption.resetCounterTooltip") || "Resetear contador"}>
                          <IconButton size="small" onClick={() => handleResetConsumption("messages")}>
                            <ResetIcon fontSize="small" style={{ color: theme.palette.primary.main }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </Grid>

                    {/* Estados */}
                    <Grid item xs={12} sm={6} md={4}>
                      <div className={classes.counterCard}>
                        <Box>
                          <Typography className={classes.counterTitle}>
                            {i18n.t("companyModal.consumption.statusPosts")}
                          </Typography>
                          <Typography className={classes.counterValue}>
                            {record.consumStatusPostsMonth || 0} / {values.customMaxStatusPosts !== "" && values.customMaxStatusPosts !== null ? `${values.customMaxStatusPosts} (pers.)` : (record.plan?.maxStatusPosts === -1 ? "Ilimitado" : (record.plan?.maxStatusPosts || 0))}
                          </Typography>
                        </Box>
                        <Tooltip title={i18n.t("companyModal.consumption.resetCounterTooltip") || "Resetear contador"}>
                          <IconButton size="small" onClick={() => handleResetConsumption("statusPosts")}>
                            <ResetIcon fontSize="small" style={{ color: theme.palette.primary.main }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </Grid>

                    {/* Grupos */}
                    <Grid item xs={12} sm={6} md={4}>
                      <div className={classes.counterCard}>
                        <Box>
                          <Typography className={classes.counterTitle}>
                            {i18n.t("companyModal.consumption.groupPosts")}
                          </Typography>
                          <Typography className={classes.counterValue}>
                            {record.consumGroupPostsMonth || 0} / {values.customMaxGroupPosts !== "" && values.customMaxGroupPosts !== null ? `${values.customMaxGroupPosts} (pers.)` : (record.plan?.maxGroupPosts === -1 ? "Ilimitado" : (record.plan?.maxGroupPosts || 0))}
                          </Typography>
                        </Box>
                        <Tooltip title={i18n.t("companyModal.consumption.resetCounterTooltip") || "Resetear contador"}>
                          <IconButton size="small" onClick={() => handleResetConsumption("groupPosts")}>
                            <ResetIcon fontSize="small" style={{ color: theme.palette.primary.main }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </Grid>

                    {/* Comunidades */}
                    <Grid item xs={12} sm={6} md={4}>
                      <div className={classes.counterCard}>
                        <Box>
                          <Typography className={classes.counterTitle}>
                            {i18n.t("companyModal.consumption.communityPosts")}
                          </Typography>
                          <Typography className={classes.counterValue}>
                            {record.consumCommunityPostsMonth || 0} / {values.customMaxCommunityPosts !== "" && values.customMaxCommunityPosts !== null ? `${values.customMaxCommunityPosts} (pers.)` : (record.plan?.maxCommunityPosts === -1 ? "Ilimitado" : (record.plan?.maxCommunityPosts || 0))}
                          </Typography>
                        </Box>
                        <Tooltip title={i18n.t("companyModal.consumption.resetCounterTooltip") || "Resetear contador"}>
                          <IconButton size="small" onClick={() => handleResetConsumption("communityPosts")}>
                            <ResetIcon fontSize="small" style={{ color: theme.palette.primary.main }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </Grid>
                  </Grid>
                </div>
              )}

              {/* SECCIÓN 4: LÍMITES PERSONALIZADOS */}
              {record.id && (
                <div className={classes.filterColumnBox}>
                  <Typography className={classes.filterSectionSubheader}>
                    <SpeedIcon />
                    {i18n.t("companyModal.consumption.customLimitsTitle") || "Límites Personalizados para esta Empresa"}
                  </Typography>
                  <Typography variant="caption" style={{ color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.6)" : "#64748B", display: "block", marginBottom: 12 }}>
                    {i18n.t("companyModal.consumption.customLimitsHint") || "Ingresa un valor numérico para sobrescribir el plan asignado. Deja vacío para usar el valor por defecto."}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        as={TextField}
                        label={i18n.t("companyModal.consumption.customContacts")}
                        name="customMaxContacts"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={record.plan?.maxContacts !== undefined ? String(record.plan.maxContacts) : (i18n.t("companyModal.consumption.planDefaultPlaceholder") || "Def. Plan")}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        as={TextField}
                        label={i18n.t("companyModal.consumption.customMessages")}
                        name="customMaxMessages"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={record.plan?.messagesLimit !== undefined ? String(record.plan.messagesLimit) : (i18n.t("companyModal.consumption.planDefaultPlaceholder") || "Def. Plan")}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        as={TextField}
                        label={i18n.t("companyModal.consumption.customFlows")}
                        name="customMaxFlows"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={record.plan?.maxFlows !== undefined ? String(record.plan.maxFlows) : (i18n.t("companyModal.consumption.planDefaultPlaceholder") || "Def. Plan")}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        as={TextField}
                        label={i18n.t("companyModal.consumption.customEmails")}
                        name="customMaxEmails"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={record.plan?.maxConnEmail !== undefined ? String(record.plan.maxConnEmail) : (i18n.t("companyModal.consumption.planDefaultPlaceholder") || "Def. Plan")}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        as={TextField}
                        label={i18n.t("companyModal.consumption.customStatusPosts")}
                        name="customMaxStatusPosts"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={record.plan?.maxStatusPosts !== undefined ? String(record.plan.maxStatusPosts) : (i18n.t("companyModal.consumption.planDefaultPlaceholder") || "Def. Plan")}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        as={TextField}
                        label={i18n.t("companyModal.consumption.customGroupPosts")}
                        name="customMaxGroupPosts"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={record.plan?.maxGroupPosts !== undefined ? String(record.plan.maxGroupPosts) : (i18n.t("companyModal.consumption.planDefaultPlaceholder") || "Def. Plan")}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        as={TextField}
                        label={i18n.t("companyModal.consumption.customCommunityPosts")}
                        name="customMaxCommunityPosts"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={record.plan?.maxCommunityPosts !== undefined ? String(record.plan.maxCommunityPosts) : (i18n.t("companyModal.consumption.planDefaultPlaceholder") || "Def. Plan")}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        as={TextField}
                        label={i18n.t("companyModal.consumption.customWebWidgets")}
                        name="customMaxWebWidgets"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={record.plan?.maxWebWidgets !== undefined ? String(record.plan.maxWebWidgets) : (i18n.t("companyModal.consumption.planDefaultPlaceholder") || "Def. Plan")}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </div>
              )}
            </DialogContent>

            <DialogActions className={classes.filterModalActions}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  style={{ color: theme.palette.type === "dark" ? "#aaa" : "#64748B", borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)", borderRadius: 8, textTransform: "none" }}
                >
                  {i18n.t("companyModal.buttons.close") || "Cerrar"}
                </Button>
                {record.id && record.id !== 1 && (
                  <Button
                    onClick={() => onDelete(record)}
                    variant="outlined"
                    style={{ color: "#ff5252", borderColor: "rgba(255, 82, 82, 0.3)", borderRadius: 8, textTransform: "none" }}
                  >
                    {i18n.t("compaies.table.delete")}
                  </Button>
                )}
                {record.id && (
                  <Button
                    onClick={handleOpenModalUsers}
                    variant="outlined"
                    style={{ color: theme.palette.primary.main, borderColor: `${theme.palette.primary.main}4D`, borderRadius: 8, textTransform: "none" }}
                  >
                    {i18n.t("companyModal.buttons.users") || "Usuarios"}
                  </Button>
                )}
              </div>

              <ButtonWithSpinner
                loading={loading}
                type="submit"
                className={classes.applyButton}
              >
                {i18n.t("compaies.table.save") || "Guardar Cambios"}
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>

      <ModalUsers
        userId={firstUser.id}
        companyId={record.id}
        open={modalUser}
        onClose={() => setModalUser(false)}
      />
      {record.id && (
        <ResellerTransactionModal
          open={transactionModalOpen}
          onClose={() => setTransactionModalOpen(false)}
          companyId={record.id}
        />
      )}
    </Dialog>
  );
}

export function CompaniesManagerGrid(props) {
  const { records, onSelect, onDelete, onRecalculateStorage, onCompanyUpdated, order, orderBy, onRequestSort } = props;
  const classes = useStyles();
  const history = useHistory();

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  const { dateToClient, datetimeToClient } = useDate();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const [currency, setCurrency] = useState("$");
  const { getAll: getAllSettings } = useSettings();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (user && user.companyId) {
          const settings = await getAllSettings(user.companyId);
          const systemCurrency = settings.find((s) => s.key === "system_currency");
          if (systemCurrency && systemCurrency.value) {
            setCurrency(systemCurrency.value);
          } else {
            setCurrency("$");
          }
        }
      } catch (error) {
        console.error("Error fetching system currency:", error);
        setCurrency("$");
      }
    };
    fetchSettings();
  }, [user]);

  const handleImpersonate = async (company) => {
    const adminToken = localStorage.getItem("token");
    if (adminToken) {
      localStorage.setItem("adminToken", adminToken);
    }

    try {
      const { data } = await api.post("/auth/impersonate", { companyId: company.id });

      localStorage.setItem("token", JSON.stringify(data.token));
      localStorage.setItem("isImpersonating", "true");

      toast.success(i18n.t("compaies.toasts.impersonating", { name: company.name }) || `Accediendo como administrador a ${company.name}...`);
      setTimeout(() => {
        history.push("/");
        window.location.reload();
      }, 500);
    } catch (err) {
      toastError(err);
      localStorage.removeItem("adminToken");
    }
  };

  const handleResendCompanyActivation = async (company) => {
    try {
      const { data } = await api.post(`/companies/${company.id}/resend-activation`);
      toast.success(data?.message || i18n.t("compaies.toasts.activationResent", { email: company.email }) || `Correo de activación reenviado a ${company.email}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleDirectActivate = async (company) => {
    try {
      await api.put(`/companies/${company.id}`, { ...company, status: true });
      if (typeof onCompanyUpdated === "function") {
        onCompanyUpdated({ ...company, status: true });
      }
      toast.success(i18n.t("compaies.toasts.companyActivated", { name: company.name }) || `Empresa ${company.name} activada con éxito`);
    } catch (err) {
      toastError(err);
    }
  };

  const renderStatus = (row) => {
    if (row.status === false) {
      return (
        <Chip
          label={i18n.t("compaies.status.pending") || "Pendiente"}
          size="small"
          style={{
            backgroundColor: "rgba(255, 145, 0, 0.15)",
            color: "#FF9100",
            border: "1px solid rgba(255, 145, 0, 0.4)",
            fontWeight: 700,
            fontSize: "0.72rem",
            whiteSpace: "nowrap",
          }}
        />
      );
    }
    const isDark = theme.palette.type === "dark";
    return (
      <Chip
        label={i18n.t("compaies.status.active") || "Activa"}
        size="small"
        style={{
          backgroundColor: isDark ? "rgba(16, 185, 129, 0.15)" : "#ECFDF5",
          color: isDark ? "#10B981" : "#047857",
          border: isDark ? "1px solid rgba(16, 185, 129, 0.4)" : "1px solid #A7F3D0",
          fontWeight: 700,
          fontSize: "0.72rem",
          whiteSpace: "nowrap",
        }}
      />
    );
  };

  const renderPlan = (row) => {
    return (row.planId !== null && row.plan) ? row.plan.name : "-";
  };

  const renderPlanValue = (row) => {
    return (row.planId !== null && row.plan) ? (row.plan.amount ? row.plan.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00') : "-";
  };

  const renderLastLogin = (lastLogin) => {
    if (!lastLogin) return "-";
    const dt = datetimeToClient(lastLogin);
    const parts = dt.split(" ");
    if (parts.length >= 2) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.25 }}>
          <span style={{ fontSize: "0.75rem", fontWeight: 500 }}>{parts[0]}</span>
          <span style={{ fontSize: "0.68rem", color: theme.palette.type === "dark" ? "rgba(255,255,255,0.6)" : "#666" }}>{parts.slice(1).join(" ")}</span>
        </div>
      );
    }
    return <span style={{ fontSize: "0.75rem" }}>{dt}</span>;
  };

  const renderStorageUsage = (row) => {
    const usedBytes = row.folderSize ? parseInt(row.folderSize) : (row.metrics?.folderSize || 0);
    const limitBytes = row.storageLimit || 209715200; // 200MB default
    const percentage = Math.min((usedBytes / limitBytes) * 100, 100);

    const isDark = theme.palette.type === "dark";
    let barColor = isDark ? "#10B981" : "#059669";
    if (percentage > 80) barColor = isDark ? "#F59E0B" : "#D97706";
    if (percentage > 95) barColor = isDark ? "#EF4444" : "#DC2626";

    const usedMB = (usedBytes / (1024 * 1024)).toFixed(2);
    const limitMB = (limitBytes / (1024 * 1024)).toFixed(0);

    return (
      <div style={{ width: "100%", minWidth: 80, maxWidth: 125 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2, fontSize: "0.68rem" }}>
          <span style={{ color: theme.palette.type === "dark" ? "rgba(255,255,255,0.6)" : "#666" }}>
            {i18n.t("compaies.storage.assigned") || "Asignado"}:
          </span>
          <span style={{ fontWeight: 600, color: theme.palette.type === "dark" ? "rgba(255,255,255,0.85)" : "#333" }}>
            {`${limitMB} MB`}
          </span>
        </div>
        <div style={{ width: "100%", height: 5, backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${percentage}%`, height: "100%", backgroundColor: barColor, borderRadius: 3, transition: "width 0.3s ease" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2, fontSize: "0.68rem" }}>
          <span style={{ color: theme.palette.type === "dark" ? "rgba(255,255,255,0.6)" : "#666" }}>
            {i18n.t("compaies.storage.used") || "Utilizado"}:
          </span>
          <span style={{ fontWeight: 600, color: barColor }}>
            {`${usedMB} MB`}
          </span>
        </div>
      </div>
    );
  };

  const rowStyle = (record) => {
    let style = {};
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        style.backgroundColor = "rgba(255, 214, 0, 0.08)";
      }
      if (diff <= 0) {
        style.backgroundColor = "rgba(255, 23, 68, 0.1)";
      }
    }

    const isResellerGroup = record.resellerId || (record.discountPercentage > 0 ? record.id : 0);
    if (isResellerGroup !== 0) {
      style.borderLeft = `4px solid ${theme.palette.primary.main}`;
      if (!style.backgroundColor) {
        style.backgroundColor = `${theme.palette.primary.main}0A`;
      }
    }
    return style;
  };

  const getNeonClass = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return classes.dueDateYellow;
      }
      if (diff <= 0) {
        return classes.dueDateRed;
      }
    }
    return classes.dueDateGreen;
  };

  return (
    <div className={classes.tableWrapper}>
      <Table stickyHeader className={classes.table} size="small">
        <colgroup>
          <col style={{ width: "3%" }} />
          <col style={{ width: "5.5%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "7.5%" }} />
          <col style={{ width: "7.5%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "10.5%" }} />
          <col style={{ width: "7%" }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeader} align="center">
              <TableSortLabel
                active={orderBy === "id"}
                direction={orderBy === "id" ? order : "asc"}
                onClick={createSortHandler("id")}
                className={classes.tableHeaderSort}
              >
                #
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHeader} align="center">
              {i18n.t("compaies.table.status") || "Estado"}
            </TableCell>
            <TableCell className={classes.tableHeader} align="left">
              <TableSortLabel
                active={orderBy === "name"}
                direction={orderBy === "name" ? order : "asc"}
                onClick={createSortHandler("name")}
                className={classes.tableHeaderSort}
              >
                {i18n.t("compaies.table.name")}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHeader} align="left">
              <TableSortLabel
                active={orderBy === "email"}
                direction={orderBy === "email" ? order : "asc"}
                onClick={createSortHandler("email")}
                className={classes.tableHeaderSort}
              >
                {i18n.t("compaies.table.email")}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHeader} align="center">{i18n.t("compaies.table.phone")}</TableCell>
            <TableCell className={classes.tableHeader} align="center">{i18n.t("compaies.table.plan")}</TableCell>
            <TableCell className={classes.tableHeader} align="center">{i18n.t("compaies.table.value")}</TableCell>
            <TableCell className={classes.tableHeader} align="center">
              <TableSortLabel
                active={orderBy === "createdAt"}
                direction={orderBy === "createdAt" ? order : "asc"}
                onClick={createSortHandler("createdAt")}
                className={classes.tableHeaderSort}
              >
                {i18n.t("compaies.table.createdAt")}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHeader} align="center">
              <TableSortLabel
                active={orderBy === "dueDate"}
                direction={orderBy === "dueDate" ? order : "asc"}
                onClick={createSortHandler("dueDate")}
                className={classes.tableHeaderSort}
              >
                {i18n.t("compaies.table.dueDate")}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHeader} align="center">{i18n.t("compaies.table.reseller") || "Reseller"}</TableCell>
            <TableCell className={classes.tableHeader} align="center">{i18n.t("compaies.table.lastLogin")}</TableCell>
            <TableCell className={classes.tableHeader} align="center">{i18n.t("compaies.table.diskUsage")}</TableCell>
            <TableCell className={classes.tableHeader} align="center">
              {i18n.t("compaies.table.actions") || "Acciones"}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row) => (
            <TableRow className={classes.tableRow} style={rowStyle(row)} key={row.id}>
              <TableCell className={classes.nowrap} align="center" style={{ fontWeight: 600 }}>
                {row.id}
              </TableCell>
              <TableCell className={classes.nowrap} align="center" style={{ minWidth: 125 }}>
                {renderStatus(row)}
              </TableCell>
              <TableCell align="left" style={{ fontWeight: 600, maxWidth: 130, lineHeight: 1.25, whiteSpace: "normal", wordBreak: "break-word" }}>
                {row.name || "-"}
              </TableCell>
              <TableCell align="left" style={{ maxWidth: 140, lineHeight: 1.2, whiteSpace: "normal", wordBreak: "break-word", color: theme.palette.type === "dark" ? "rgba(255,255,255,0.85)" : "#333", fontSize: "0.74rem" }}>
                {row.email || "-"}
              </TableCell>
              <TableCell className={classes.nowrap} align="center">{row.phone || "-"}</TableCell>
              <TableCell className={classes.nowrap} align="center">
                <Chip size="small" label={renderPlan(row)} variant="outlined" style={{ borderColor: `${theme.palette.primary.main}66`, color: theme.palette.primary.main, backgroundColor: `${theme.palette.primary.main}14`, fontWeight: 700, fontSize: "0.7rem", maxWidth: "100%", height: 22 }} />
              </TableCell>
              <TableCell className={classes.nowrap} align="center" style={{ fontWeight: 600 }}>
                {currency} {renderPlanValue(row)}
              </TableCell>
              <TableCell className={classes.nowrap} align="center">{dateToClient(row.createdAt)}</TableCell>
              <TableCell className={classes.nowrap} align="center">
                <span className={getNeonClass(row)}>
                  {dateToClient(row.dueDate)}
                </span>
              </TableCell>
              <TableCell className={classes.nowrap} align="center">
                {row.discountPercentage > 0 ? (
                  <Chip size="small" label="Reseller Principal" style={{ backgroundColor: "rgba(124, 77, 255, 0.2)", color: "#B388FF", fontWeight: 700, fontSize: "0.66rem", maxWidth: "100%", height: 22 }} />
                ) : row.resellerId ? (
                  <Chip size="small" label={`Reseller #${row.resellerId}`} variant="outlined" style={{ borderColor: "rgba(124, 77, 255, 0.4)", color: "#B388FF", fontSize: "0.66rem", maxWidth: "100%", height: 22 }} />
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell align="center">
                {renderLastLogin(row.lastLogin)}
              </TableCell>
              <TableCell align="center" style={{ padding: "6px 2px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  {renderStorageUsage(row)}
                  <Tooltip title={i18n.t("compaies.buttons.recalculateStorage") || "Recalcular Espacio"}>
                    <IconButton
                      size="small"
                      style={{ color: theme.palette.primary.main, padding: 2 }}
                      onClick={() => onRecalculateStorage(row)}
                    >
                      <RefreshIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              </TableCell>
              <TableCell className={classes.nowrap} align="center">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Tooltip title={i18n.t("compaies.tooltips.impersonate") || "Ingresar como Admin (Soporte)"}>
                    <IconButton
                      size="small"
                      className={`${classes.actionButton} ${classes.btnKey}`}
                      onClick={() => handleImpersonate(row)}
                    >
                      <VpnKeyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {!row.status && (
                    <>
                      <Tooltip title={i18n.t("compaies.tooltips.activateDirectly") || "Activar Empresa Directamente"}>
                        <IconButton
                          size="small"
                          className={`${classes.actionButton} ${classes.btnActivate}`}
                          onClick={() => handleDirectActivate(row)}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={i18n.t("compaies.tooltips.resendActivation") || "Reenviar correo de activación"}>
                        <IconButton
                          size="small"
                          className={`${classes.actionButton} ${classes.btnMail}`}
                          onClick={() => handleResendCompanyActivation(row)}
                        >
                          <MailOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                  <Tooltip title={i18n.t("compaies.tooltips.edit") || "Editar Empresa"}>
                    <IconButton
                      size="small"
                      className={`${classes.actionButton} ${classes.btnEdit}`}
                      onClick={() => onSelect(row)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {row.id !== 1 && (
                    <Tooltip title={i18n.t("compaies.tooltips.delete") || "Eliminar Empresa"}>
                      <IconButton
                        size="small"
                        className={`${classes.actionButton} ${classes.btnDelete}`}
                        onClick={() => onDelete(row)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function CompaniesManager() {
  const classes = useStyles();
  const theme = useTheme();
  const { list, save, update, remove, recalculateFolderSize } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    dueDate: "",
    recurrence: "",
    password: "",
    generateInvoice: true,
    enableVision: true,
    customMaxContacts: "",
    customMaxMessages: "",
    customMaxFlows: "",
    customMaxEmails: "",
    customMaxStatusPosts: "",
    customMaxGroupPosts: "",
    customMaxCommunityPosts: "",
    customMaxWebWidgets: "",
    consumContacts: 0,
    consumMessagesMonth: 0,
    consumStatusPostsMonth: 0,
    consumGroupPostsMonth: 0,
    consumCommunityPostsMonth: 0,
    plan: null,
  });

  const { user } = useContext(AuthContext);

  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        return;
      }
      setLoading(true);
      try {
        const companyList = await list();
        setRecords(companyList);
      } catch (e) {
        toastError(e);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  const handleOpenDeleteDialog = (company) => {
    setDeletingRecord(company);
    setShowConfirmDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingRecord) return;
    setLoading(true);
    try {
      await remove(deletingRecord.id);
      setRecords((prev) => prev.filter((r) => r.id !== deletingRecord.id));
      toast.success(i18n.t("compaies.toasts.deleted"));
      setShowConfirmDialog(false);
      setModalOpen(false);
    } catch (e) {
      toastError(e);
    }
    setLoading(false);
  };

  const handleOpenNew = () => {
    setRecord({
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      dueDate: "",
      recurrence: "MENSAL",
      password: "",
      generateInvoice: true,
      enableVision: true,
      customMaxContacts: "",
      customMaxMessages: "",
      customMaxFlows: "",
      customMaxEmails: "",
      customMaxStatusPosts: "",
      customMaxGroupPosts: "",
      customMaxCommunityPosts: "",
      customMaxWebWidgets: "",
      consumContacts: 0,
      consumMessagesMonth: 0,
      consumStatusPostsMonth: 0,
      consumGroupPostsMonth: 0,
      consumCommunityPostsMonth: 0,
      plan: null,
      storageLimit: 200,
    });
    setModalOpen(true);
  };

  const handleSelect = (data) => {
    setRecord({
      ...data,
      customMaxContacts: data.customMaxContacts !== null && data.customMaxContacts !== undefined ? data.customMaxContacts : "",
      customMaxMessages: data.customMaxMessages !== null && data.customMaxMessages !== undefined ? data.customMaxMessages : "",
      customMaxFlows: data.customMaxFlows !== null && data.customMaxFlows !== undefined ? data.customMaxFlows : "",
      customMaxEmails: data.customMaxEmails !== null && data.customMaxEmails !== undefined ? data.customMaxEmails : "",
      customMaxStatusPosts: data.customMaxStatusPosts !== null && data.customMaxStatusPosts !== undefined ? data.customMaxStatusPosts : "",
      customMaxGroupPosts: data.customMaxGroupPosts !== null && data.customMaxGroupPosts !== undefined ? data.customMaxGroupPosts : "",
      customMaxCommunityPosts: data.customMaxCommunityPosts !== null && data.customMaxCommunityPosts !== undefined ? data.customMaxCommunityPosts : "",
      customMaxWebWidgets: data.customMaxWebWidgets !== null && data.customMaxWebWidgets !== undefined ? data.customMaxWebWidgets : "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
        setRecords((prev) => prev.map((r) => (r.id === data.id ? { ...r, ...data } : r)));
        toast.success(i18n.t("compaies.toasts.success"));
      } else {
        const newCompany = await save(data);
        setRecords((prev) => [newCompany, ...prev]);
        toast.success(i18n.t("compaies.toasts.success"));
      }
      setModalOpen(false);
    } catch (e) {
      toastError(e);
    }
    setLoading(false);
  };

  const handleRecalculateStorage = async (company) => {
    setLoading(true);
    try {
      const response = await recalculateFolderSize(company.id);
      setRecords((prev) =>
        prev.map((r) => {
          if (r.id === company.id) {
            return {
              ...r,
              folderSize: response.company.folderSize,
              numberFileFolder: response.company.numberFileFolder,
              updatedAtFolder: response.company.updatedAtFolder,
              metrics: response.company.metrics,
            };
          }
          return r;
        })
      );
      toast.success(i18n.t("compaies.toasts.success") || response.message);
    } catch (e) {
      toastError(e);
    }
    setLoading(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredRecords = records.filter((r) => {
    if (!searchParam) return true;
    const term = searchParam.toLowerCase();
    return (
      (r.name && r.name.toLowerCase().includes(term)) ||
      (r.email && r.email.toLowerCase().includes(term)) ||
      (r.phone && r.phone.toLowerCase().includes(term)) ||
      (r.id && String(r.id).includes(term))
    );
  });

  const sortedRecords = [...filteredRecords].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    if (orderBy === "id") {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    } else if (orderBy === "name" || orderBy === "email") {
      aVal = (aVal || "").toLowerCase();
      bVal = (bVal || "").toLowerCase();
    } else if (orderBy === "createdAt" || orderBy === "dueDate") {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }

    if (order === "desc") {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    } else {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    }
  });

  return (
    <div className={classes.mainContainer}>
      {/* BARRA SUPERIOR MODERNA HOMOLOGADA */}
      <Paper elevation={0} className={classes.topBar}>
        <div className={classes.topBarLeft}>
          <Typography className={classes.topBarTitle}>
            <BusinessIcon className={classes.topBarIcon} />
            {i18n.t("compaies.systemCompanies") || "Empresas del Sistema"}
          </Typography>
          <Chip
            label={i18n.t("compaies.registeredCompanies", { count: sortedRecords.length }) || `Empresas Registradas: ${sortedRecords.length}`}
            className={classes.countChip}
            size="small"
          />
        </div>

        <div className={classes.topBarRight}>
          <TextField
            placeholder={i18n.t("compaies.searchPlaceholder") || "Buscar empresa por nombre, correo, WhatsApp o ID..."}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
          />


        </div>
      </Paper>

      {/* GRID DE EMPRESAS CON CABECERA FIJA */}
      <CompaniesManagerGrid
        records={sortedRecords}
        onSelect={handleSelect}
        onDelete={handleOpenDeleteDialog}
        onRecalculateStorage={handleRecalculateStorage}
        onCompanyUpdated={(updatedCompany) => {
          setRecords((prev) =>
            prev.map((c) =>
              c.id === updatedCompany.id
                ? { ...c, ...updatedCompany, plan: updatedCompany.plan || c.plan }
                : c
            )
          );
        }}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
      />

      {/* MODAL FLOTANTE DE EDICIÓN */}
      <CompanyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialValue={record}
        onSubmit={handleSubmit}
        onDelete={handleOpenDeleteDialog}
        loading={loading}
        onResetConsumption={(updatedCompany) => {
          setRecord((prev) => ({
            ...prev,
            ...updatedCompany,
            plan: updatedCompany.plan || prev.plan,
          }));
          setRecords((prev) =>
            prev.map((c) =>
              c.id === updatedCompany.id
                ? { ...c, ...updatedCompany, plan: updatedCompany.plan || c.plan }
                : c
            )
          );
        }}
      />

      {/* MODAL DE CONFIRMACIÓN DE ELIMINACIÓN */}
      <ConfirmationModal
        title={i18n.t("compaies.confirmationModal.deleteTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleDelete}
      >
        {i18n.t("compaies.confirmationModal.deleteMessage")}
      </ConfirmationModal>
    </div>
  );
}
